import { useMemo } from 'react';
import { Card, CardContent, CardHeader, ErrorBanner, Spinner } from '@/components/ui';
import { portfolioApi } from '@/services/portfolio-api';
import type { IIssueInProgressDailyTrendRow } from '@skystone/shared';
import { adaptWipTrendPointsToLegacyRows } from '@/lib/wip-trend-adapter';
import type { IMetricViewFilters } from '@/hooks/use-metric-view-filters';
import { useCardFetch } from '@/hooks/use-card-fetch';
import { useCardWidth } from '@/hooks/use-card-width';
import { DeltaLabel, formatWowText } from '@/components/shared/delta-label';
import { SummaryTile } from './SummaryTile';
import { SummaryColumn, issueTypeLabel } from './summary-layout';
import { CARD_WIDTH_THRESHOLDS } from '@/lib/metric-view/responsive-thresholds';
import {
  wipWeeklyAverage,
  monthlyAverageByType,
  ISSUE_TYPES,
} from '@/lib/metric-view/aggregation';
import { formatPeriodLabel, rollingAvgLabel } from '@/lib/metric-view/period-label';

interface IWipSummaryCardProps {
  filters: IMetricViewFilters;
}

const ROLLING_WEEKS = 4;

export function WipSummaryCard({ filters }: Readonly<IWipSummaryCardProps>) {
  const { ref, width } = useCardWidth<HTMLDivElement>();
  const stackCols = width > 0 && width < CARD_WIDTH_THRESHOLDS.wipSummary.stackedCols;
  // PRODOPSAI-418: canonical `portfolioApi.getWipTrend` returns the same
  // breakdown the helpers below expect; the adapter renames fields so
  // `wipWeeklyAverage` / `monthlyAverageByType` stay untouched.
  const { data: rows, loading, error } = useCardFetch<IIssueInProgressDailyTrendRow[]>(
    () => portfolioApi.getWipTrend({
      from: filters.from,
      to: filters.to,
      productId: filters.productId || undefined,
      teamId: filters.teamId || undefined,
    }).then(adaptWipTrendPointsToLegacyRows),
    [filters.from, filters.to, filters.productId, filters.teamId],
    'Failed to load WIP summary.',
  );

  const weekly = useMemo(() => (rows ? wipWeeklyAverage(rows, ROLLING_WEEKS) : null), [rows]);
  const monthly = useMemo(() => (rows ? monthlyAverageByType(rows) : null), [rows]);

  const periodLabel = formatPeriodLabel(filters.pill);
  const weeklyLabel = rollingAvgLabel(filters.from, filters.to, ROLLING_WEEKS);

  return (
    <Card ref={ref}>
      <CardHeader>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">WIP Summary</h3>
        <p className="text-[11px] text-muted-foreground">
          Average work-in-progress per week and per month — lower is generally better.
        </p>
      </CardHeader>
      <CardContent>
        {loading && <div className="flex h-40 items-center justify-center"><Spinner /></div>}
        {!loading && error && <ErrorBanner message={error} />}
        {!loading && !error && weekly && monthly && (
          <div className={stackCols ? 'grid grid-cols-1 gap-4' : 'grid grid-cols-1 gap-4 sm:grid-cols-2'}>
            <SummaryColumn title="Weekly average" subtitle={weeklyLabel}>
              {ISSUE_TYPES.map((t) => (
                <SummaryTile
                  key={t}
                  value={weekly.avg[t].toLocaleString()}
                  label={issueTypeLabel(t)}
                  delta={
                    <DeltaLabel
                      direction={weekly.wow[t].direction}
                      text={`${formatWowText(weekly.wow[t].deltaValue)} WoW`}
                      higherIsBetter={false}
                    />
                  }
                />
              ))}
            </SummaryColumn>

            <SummaryColumn title="Monthly average" subtitle={`Avg WIP per month · ${periodLabel}`}>
              {ISSUE_TYPES.map((t) => (
                <SummaryTile
                  key={t}
                  value={monthly[t].toLocaleString()}
                  label={issueTypeLabel(t)}
                  delta={<DeltaLabel direction={null} text="—" />}
                />
              ))}
            </SummaryColumn>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
